<script setup lang="ts">
import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import type { CalendarRestriction, DateRange } from '@/types/SearchBar.ts';
import type { DurationMode } from '@/utils/filters/durationMode.ts';
import { $t } from '@/composables/translations/useTranslate.ts';
import BaseSpinner from '@/components/ui/BaseSpinner.vue';
import { useRestrictedRangeSelection } from '@/composables/calendar/useRestrictedRangeSelection.ts';
import { formatDateLocal } from '@/utils/formatting/dateFormatter.ts';
import { MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan.ts';
import { keyToLocalDate } from '@/utils/calendar/hotelAvailability.ts';

interface Props {
  modelValue?: DateRange;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  locale?: string;
  wrapperClass?: string;
  calendarClass?: string;
  restriction?: CalendarRestriction;
  durationMode?: DurationMode;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  minDate: () => new Date(),
  maxDate: () => {
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    return nextYear;
  },
  locale: 'de',
  wrapperClass: '',
  calendarClass: '',
  restriction: undefined,
  durationMode: 'preset',
});

const emit = defineEmits<{
  'update:modelValue': [dateRange: DateRange];
  select: [dateRange: DateRange];
  'pending-change': [pending: boolean];
}>();

const HINT_DURATION_MS = 4000;
const STAY_SPINNER_DELAY_MS = 300;

const localDateRange = ref<DateRange>(props.modelValue);
const pickerModel = ref<Date[] | null>(cloneRange(props.modelValue));
const datepickerRef = ref<InstanceType<typeof VueDatePicker> | null>(null);
const rootRef = ref<HTMLElement | null>(null);
const bubbleRef = ref<HTMLElement | null>(null);

const hint = ref<{ text: string; anchor: string } | null>(null);
const liveText = ref('');
const bubbleStyle = ref<Record<string, string> | null>(null);
let hintTimer: ReturnType<typeof setTimeout> | null = null;
const showStaySpinner = ref(false);
let staySpinnerTimer: ReturnType<typeof setTimeout> | null = null;

const multiCalendarConfig = computed(() => ({
  count: 2,
}));

function cloneRange(range: DateRange): Date[] | null {
  return range ? [new Date(range[0]), new Date(range[1])] : null;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? keyToLocalDate(value) : new Date(value);
}

const arrivalMaxDate = computed(() => toDate(props.maxDate));

const selection = useRestrictedRangeSelection({
  restriction: () => props.restriction,
  mode: () => props.durationMode,
  maxDate: () => arrivalMaxDate.value,
  setPendingModel: (start) => {
    pickerModel.value = [start];
  },
  restoreConfirmed: () => {
    void restoreConfirmed();
  },
  showHint,
});

/**
 * Arrivals stop at `maxDate`, but a departure or window end may run up to
 * {@link MAX_PRODUCT_DURATION} nights past it, so the library's own limit moves out.
 */
const libraryMaxDate = computed(() => {
  const max = arrivalMaxDate.value;
  if (!max || !selection.active.value) return props.maxDate || undefined;
  const extended = new Date(max);
  extended.setDate(extended.getDate() + MAX_PRODUCT_DURATION);
  return extended;
});

const ariaLabels = computed(() =>
  selection.active.value
    ? {
        day: (cell: { value: Date }) => {
          const dateText = cell.value.toLocaleDateString('de-DE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
          const role = selection.ariaLabelFor(formatDateLocal(cell.value));
          return role ? `${dateText}, ${role}` : dateText;
        },
      }
    : {}
);

/**
 * The library re-syncs only on a new model reference, and a `null` that stays
 * `null` is no change at all, so an empty confirmed range goes through `[]` first.
 */
async function restoreConfirmed(): Promise<void> {
  const confirmed = cloneRange(localDateRange.value);
  if (confirmed) {
    pickerModel.value = confirmed;
    return;
  }
  pickerModel.value = [];
  await nextTick();
  pickerModel.value = null;
}

function dismissHint(): void {
  if (hintTimer) clearTimeout(hintTimer);
  hintTimer = null;
  hint.value = null;
}

/**
 * The same day also renders as a neighbour-month cell. Visible ones carry
 * `dp__cell_offset`; hidden ones (`hide-offset-dates`) lose it but have no tabindex.
 */
function findDayCell(anchor: string): HTMLElement | null {
  const cells = rootRef.value?.querySelectorAll<HTMLElement>(`[data-test-id="dp-${anchor}"]`);
  return (
    Array.from(cells ?? []).find(
      (cell) => cell.hasAttribute('tabindex') && !cell.querySelector('.dp__cell_offset')
    ) ?? null
  );
}

/** How far the bubble may reach past the picker into the modal padding. */
const BUBBLE_OVERHANG_PX = 24;

/**
 * Desktop bubble: centred under the clicked day's month, over that month's
 * sixth grid row, which is usually empty. When the row holds a day of the month
 * the bubble moves just below it instead, so it never hides a day.
 */
async function positionBubble(anchor: string): Promise<void> {
  const root = rootRef.value;
  const cell = findDayCell(anchor);
  const month = cell?.closest<HTMLElement>('.dp__instance_calendar');
  const rows = month?.querySelectorAll<HTMLElement>('.dp__calendar_row');
  const lastRow = rows?.[rows.length - 1];
  if (!root || !cell || !month || !lastRow) {
    bubbleStyle.value = null;
    return;
  }

  const rootRect = root.getBoundingClientRect();
  const monthRect = month.getBoundingClientRect();
  const rowRect = lastRow.getBoundingClientRect();
  const lastRowHasDay = !!lastRow.querySelector('[tabindex]');
  const top = lastRowHasDay ? rowRect.bottom + 4 : rowRect.top;
  const centre = monthRect.left + monthRect.width / 2 - rootRect.left;
  bubbleStyle.value = { top: `${top - rootRect.top}px`, left: `${centre}px` };

  await nextTick();
  const halfWidth = (bubbleRef.value?.offsetWidth ?? 0) / 2;
  const min = halfWidth - BUBBLE_OVERHANG_PX;
  const max = rootRect.width - halfWidth + BUBBLE_OVERHANG_PX;
  const clamped = Math.min(Math.max(centre, min), Math.max(min, max));
  if (clamped !== centre) {
    bubbleStyle.value = { ...bubbleStyle.value, left: `${clamped}px` };
  }
}

function showHint(text: string, anchor: string): void {
  if (hintTimer) clearTimeout(hintTimer);
  hint.value = { text, anchor };
  liveText.value = text;
  bubbleStyle.value = null;
  void nextTick(() => positionBubble(anchor));
  hintTimer = setTimeout(dismissHint, HINT_DURATION_MS);
}

/**
 * Programmatically scroll the calendar so the given date is visible.
 * Used by parent to focus on a holiday range after selection.
 */
async function focusDate(date: Date): Promise<void> {
  await nextTick();
  const instance = datepickerRef.value;
  if (!instance || typeof instance.setMonthYear !== 'function') return;
  instance.setMonthYear({
    month: date.getMonth(),
    year: date.getFullYear(),
  });
}

defineExpose({ focusDate });

/**
 * On open the calendar should anchor on the from-date (start of range)
 */
onMounted(() => {
  if (localDateRange.value?.[0]) {
    void focusDate(localDateRange.value[0]);
  }
});

onBeforeUnmount(() => {
  if (hintTimer) clearTimeout(hintTimer);
  if (staySpinnerTimer) clearTimeout(staySpinnerTimer);
});

watch(
  () => selection.phase.value === 'loading',
  (loading) => {
    if (staySpinnerTimer) clearTimeout(staySpinnerTimer);
    staySpinnerTimer = null;
    showStaySpinner.value = false;
    if (loading) {
      staySpinnerTimer = setTimeout(() => {
        showStaySpinner.value = true;
      }, STAY_SPINNER_DELAY_MS);
    }
  }
);

/**
 * With a restriction a range starting and ending on the same day is the user
 * clicking the pending arrival again: it cancels the selection and is never emitted.
 */
const handleDateChange = (value: DateRange) => {
  if (selection.active.value) {
    const isZeroNights = !!value && formatDateLocal(value[0]) === formatDateLocal(value[1]);
    selection.reset();
    if (isZeroNights) {
      void restoreConfirmed();
      return;
    }
  }

  localDateRange.value = value;
  pickerModel.value = cloneRange(value);
  emit('update:modelValue', value);
  emit('select', value);
};

watch(
  () => props.modelValue,
  (newValue) => {
    localDateRange.value = newValue;
    selection.reset();
    pickerModel.value = cloneRange(newValue);
  }
);

watch(
  () => [props.durationMode, selection.active.value],
  () => selection.cancelPending()
);

watch(
  () => selection.pendingStart.value !== null,
  (pending) => emit('pending-change', pending)
);

const formattedDateRange = computed(() => {
  if (!localDateRange.value || !localDateRange.value[0] || !localDateRange.value[1]) {
    return '';
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  return `${formatDate(localDateRange.value[0])} - ${formatDate(localDateRange.value[1])}`;
});
</script>

<template>
  <div
    ref="rootRef"
    :class="[
      'date-range-picker',
      wrapperClass,
      {
        'is-restricted': selection.active.value,
        'is-loading-arrivals': selection.loadingArrivals.value,
        'is-loading-stays': showStaySpinner,
      },
    ]"
    role="application"
    :aria-label="$t('ibe.datepicker.select-range', 'Select date range')"
  >
    <div
      v-if="selection.active.value && hint"
      class="calendar-hint-bar"
      aria-hidden="true"
      @click.stop="dismissHint"
    >
      <i class="w-6 h-5 bg-black icon icon-info rotate-180 shrink-0" aria-hidden="true"></i>
      <span>{{ hint.text }}</span>
    </div>

    <div class="calendar-grid">
      <VueDatePicker
        ref="datepickerRef"
        :model-value="pickerModel"
        range
        inline
        auto-apply
        six-weeks="center"
        :enable-time-picker="false"
        :multi-calendars="multiCalendarConfig"
        :min-date="minDate || undefined"
        :max-date="libraryMaxDate"
        :locale="locale"
        :class="calendarClass"
        month-name-format="long"
        :format="'dd.MM.yy'"
        :hide-offset-dates="!!restriction"
        :disabled-dates="restriction ? selection.disabledDates.value : undefined"
        :day-class="restriction ? selection.dayClass.value : undefined"
        :aria-labels="ariaLabels"
        @update:model-value="handleDateChange"
        @range-start="selection.handleRangeStart"
        @invalid-date="selection.handleInvalidDate"
      >
        <!-- Always provided: the library ignores a #day slot that appears after mount. -->
        <template #day="{ day, date }">
          <span class="calendar-day">
            <svg
              v-if="selection.arrowFor(formatDateLocal(date)) === 'arrival'"
              class="calendar-day__arrow calendar-day__arrow--arrival"
              viewBox="0 0 5 8"
              aria-hidden="true"
            >
              <path d="M0 0 5 4 0 8Z" />
            </svg>
            {{ day }}
            <svg
              v-if="selection.arrowFor(formatDateLocal(date)) === 'departure'"
              class="calendar-day__arrow calendar-day__arrow--departure"
              viewBox="0 0 5 8"
              aria-hidden="true"
            >
              <path d="M5 0 0 4 5 8Z" />
            </svg>
          </span>
        </template>
      </VueDatePicker>

      <div v-if="selection.loadingArrivals.value || showStaySpinner" class="calendar-loading">
        <BaseSpinner size="lg" />
      </div>
    </div>

    <div
      v-if="hint && bubbleStyle"
      ref="bubbleRef"
      class="calendar-hint-bubble"
      :style="bubbleStyle"
      aria-hidden="true"
      @click.stop="dismissHint"
    >
      <i class="w-6 h-5 bg-black icon icon-info rotate-180 shrink-0" aria-hidden="true"></i>
      <span>{{ hint.text }}</span>
    </div>

    <div class="sr-only" aria-live="polite">{{ liveText }}</div>

    <div v-if="formattedDateRange" class="mt-1 text-center">
      <span class="text-sm text-text-black">
        {{ $t('ibe.datepicker.selected', 'Selected:') }} {{ formattedDateRange }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.date-range-picker :deep(.dp__theme_light) {
  --dp-background-color: transparent;
  --dp-menu-border-color: transparent;
  --dp-border-color: transparent;
  --dp-text-color: #000;
  --dp-hover-color: #f9f6ee;
  --dp-hover-text-color: #6c6b3c;
  --dp-hover-icon-color: #6c6b3c;
  --dp-primary-color: #f54b25;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #ebe7db;
  --dp-range-between-dates-background-color: #feede9;
  --dp-range-between-dates-text-color: #000;
  --dp-range-between-border-color: transparent;
  --dp-border-radius: 0;
  --dp-cell-border-radius: 0;
  --dp-font-size: 14px;
  --dp-font-family: 'Onest', sans-serif;
  --dp-primary-disabled-color: #f9f6ee;
}

.date-range-picker :deep(.dp__calendar_header_item) {
  text-transform: uppercase;
  font-weight: 400;
}

.date-range-picker :deep(.dp__main) {
  justify-content: center;
}

.date-range-picker :deep(.dp__month_year_wrap button) {
  font-weight: 600;
}

.date-range-picker :deep(.dp__menu_inner) {
  gap: 1.4rem;
}

.date-range-picker :deep(.dp__overlay) {
  background-color: #ffffff;
}
/* ----- Hotel-restricted calendar ----- */

.date-range-picker {
  position: relative;
}

.calendar-grid {
  position: relative;
}

.is-loading-arrivals :deep(.dp__calendar) {
  pointer-events: none;
  opacity: 0.4;
}

/* Still clickable: the pending arrival cancels, an earlier arrival restarts. */
.is-loading-stays :deep(.dp__calendar) {
  opacity: 0.4;
}

.calendar-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f54b25;
  pointer-events: none;
}

/* Static on purpose: the triangles sit on the edges of `.dp__cell_inner` (relative), not of the number. */
.calendar-day {
  display: contents;
}

.calendar-day__arrow {
  position: absolute;
  top: 50%;
  width: 5px;
  height: 8px;
  transform: translateY(-50%);
  fill: #f54b25;
}

.calendar-day__arrow--arrival {
  left: 0;
}

.calendar-day__arrow--departure {
  right: 0;
}

.date-range-picker :deep(.dp__range_start .calendar-day__arrow),
.date-range-picker :deep(.dp__range_end .calendar-day__arrow),
.date-range-picker :deep(.dp__active_date .calendar-day__arrow) {
  fill: currentColor;
}

.is-restricted :deep(.dp__cell_inner.dp__cell_disabled.dp__range_between) {
  color: #b5b0a3;
}

.date-range-picker :deep(.dp__cell_inner.restart-target.dp__cell_disabled) {
  color: var(--dp-text-color);
  cursor: pointer;
}

.date-range-picker :deep(.dp__cell_inner.first-departure) {
  font-weight: 600;
  background-color: #feede9;
}

.date-range-picker :deep(.dp__cell_inner.min-band) {
  background-color: rgb(245 75 37 / 0.08);
  border-top: 1px dashed #f54b25;
  border-bottom: 1px dashed #f54b25;
}

.date-range-picker :deep(.dp__cell_inner.min-band-end) {
  border-right: 1px dashed #f54b25;
}

/*
 * Mobile: no reserved space. The bar floats over the line right above the
 * picker (the duration hint in DateAndDurationSelector, 1rem gap away), centred
 * on it, so covering that line for a few seconds is the only cost.
 */
.calendar-hint-bar {
  display: none;
  position: absolute;
  z-index: 10;
  left: 0;
  right: 0;
  bottom: calc(100% + 1rem + 0.625rem);
  transform: translateY(50%);
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.3;
  color: #000;
  background-color: #ffded1;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.15);
  cursor: pointer;
}

@media (max-width: 767px) {
  .calendar-hint-bar {
    display: flex;
  }
}

.calendar-hint-bubble {
  display: none;
  position: absolute;
  z-index: 10;
  width: max-content;
  max-width: 300px;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  transform: translateX(-50%);
  font-size: 0.875rem;
  line-height: 1.4;
  color: #000;
  background-color: #ffded1;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.15);
  cursor: pointer;
}

@media (min-width: 768px) {
  .calendar-hint-bubble {
    display: flex;
  }
}
</style>
